import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import Fieldset from '../../Common/Components/Fieldset'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import FormRow from '../../Common/Components/FormRow'
import Button from '../../Common/Components/Button'

import {
    SCHEDULEREPORT_DAILY_OPTION_VALUE,
    SCHEDULEREPORT_HOURLY_OPTION_VALUE,
    SCHEDULEREPORT_MONTHLY_OPTION_VALUE,
    SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE,
    SCHEDULEREPORT_ONCE_OPTION_VALUE,
    SCHEDULEREPORT_WEEKLY_OPTION_VALUE
} from './Constants'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ReportScheduling as ReportSchedulingLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { announceScheduleOpen } from '../../../NonReact/common/RxJS/ScheduleTabOptionClick'

/**
 * Component properties type.
 */
type Props = {
    isScheduleActive: boolean
    scheduleReportOn: number
    setScheduleReportOn: (newValue: number) => void
    resetSchedule: boolean
    handleResetSchedule: () => void
    deleteSchedule: boolean
    setDeleteSchedule: (newValue: boolean) => void
}

/**
 * Report Scheduling section in Scheduling tab of report configuration modal window.
 *
 * @param isScheduleActive Whether the report is scheduled.
 * @param scheduleReportOn Report property.
 * @param setScheduleReportOn Setter for corresponding report property.
 * @param resetSchedule Report property.
 * @param handleResetSchedule Handler method for reset schedule action.
 * @param deleteSchedule Report property.
 * @param setDeleteSchedule Setter for report request property.
 */
const ReportScheduling: React.FC<Props> = ({
    isScheduleActive,
    scheduleReportOn,
    setScheduleReportOn,
    resetSchedule,
    handleResetSchedule,
    deleteSchedule,
    setDeleteSchedule
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ReportScheduling')

    const scheduleRptOptions: SelectOptions<number> = [
        {
            value: SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE,
            label: language.get(ReportSchedulingLanguage.SchedulereportNotscheduledOptionLabel)
        },
        {
            value: SCHEDULEREPORT_ONCE_OPTION_VALUE,
            label: language.get(ReportSchedulingLanguage.SchedulereportOnceOptionLabel)
        },
        {
            value: SCHEDULEREPORT_HOURLY_OPTION_VALUE,
            label: language.get(ReportSchedulingLanguage.SchedulereportHourlyOptionLabel)
        },
        {
            value: SCHEDULEREPORT_DAILY_OPTION_VALUE,
            label: language.get(ReportSchedulingLanguage.SchedulereportDailyOptionLabel)
        },
        {
            value: SCHEDULEREPORT_WEEKLY_OPTION_VALUE,
            label: language.get(ReportSchedulingLanguage.SchedulereportWeeklyOptionLabel),
            className: 'select-option-weekly'
        },
        {
            value: SCHEDULEREPORT_MONTHLY_OPTION_VALUE,
            label: language.get(ReportSchedulingLanguage.SchedulereportMonthlyOptionLabel)
        }
    ]

    const handleScheduleReportOnChange = (newValue: number): void => {
        if (newValue === SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE) {
            // Change to NOT_SCHEDULED is considered a delete of schedule.
            setDeleteSchedule(true)
            setScheduleReportOn(newValue)
        } else {
            // Any other change should update the value and update deleteSchedule flag to false.
            setDeleteSchedule(false)
            setScheduleReportOn(newValue)
        }
    }

    return (
        <Fieldset>
            <Heading level={HeadingLevel.ONE}>
                {language.get(ReportSchedulingLanguage.ReportScheduling_SectionHeader)}
            </Heading>
            <FormRow isInline className={'schedule-report'}>
                <Select
                    classNames='schedule-report-select'
                    label={language.get(ReportSchedulingLanguage.SchedulereportLabel)}
                    value={scheduleReportOn}
                    handleChange={handleScheduleReportOnChange}
                    options={scheduleRptOptions}
                    onOpen={announceScheduleOpen}
                />
                {isScheduleActive && (
                    <React.Fragment>
                        {!deleteSchedule && (
                            <Button
                                label={language.get(ReportSchedulingLanguage.SchedulereportResetButtonLabel)}
                                customClass='reset-schedule-button'
                                isDisabled={resetSchedule}
                                isOutlined={false}
                                onClick={handleResetSchedule}
                            />
                        )}
                    </React.Fragment>
                )}
            </FormRow>
        </Fieldset>
    )
}

export default ReportScheduling
